import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useState } from 'react'

import { type Taxonomia, type Termino, useTaxonomias, useTerminosDe } from '../api/estructura'
import { ErrorDeLaApi, pedir } from '../api/nucleo'
import { Boton, Casilla, Esqueleto, Tarjeta, claseDeEntrada } from '../componentes/ui'
import { useEsquema } from '../motor/esquema'
import { useFormulario, useValor } from '../motor/Formulario'
import { nombresConMadre, idsDeTerminos } from './arbolDeTerminos'

/**
 * Los términos de un contenido, **una caja por taxonomía** del tipo («Distrito», «Operación»):
 * se marcan los que le tocan. Todos van al mismo campo `terminos`; lo que no es de estas
 * taxonomías (si lo hubiera) se conserva tal cual, y el núcleo vuelve a comprobar al guardar que
 * cada término sea de una taxonomía del tipo.
 *
 * Quien puede crear términos (lo dice el esquema de `terminos`) los crea aquí mismo con su nombre.
 */
export const TerminosPorTaxonomia = ({ taxonomias }: { taxonomias: number[] }) => {
  const { data, isPending } = useTaxonomias(taxonomias)
  if (isPending) return <Esqueleto filas={4} />
  const lista = data ?? []
  if (lista.length === 0) return <p className="text-sm text-tenue">Las taxonomías de este tipo no están disponibles.</p>
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {lista.map((t) => (
        <CajaDeTaxonomia key={t.id} taxonomia={t} />
      ))}
    </div>
  )
}

const CajaDeTaxonomia = ({ taxonomia }: { taxonomia: Taxonomia }) => {
  const { sitio, espacio, soloLectura } = useFormulario()
  const { valor, cambiar, error } = useValor<unknown[]>('terminos')
  const { data: terminos, isPending } = useTerminosDe(taxonomia.id, sitio)
  const { data: esquemaDeTerminos } = useEsquema('terminos')
  const clienteDeConsultas = useQueryClient()
  const [filtro, setFiltro] = useState('')
  const [nuevo, setNuevo] = useState('')
  const [creando, setCreando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)

  const elegidos = idsDeTerminos(valor)
  const nombres = nombresConMadre(terminos ?? [])
  // En árbol: cada madre antes que sus hijas («Lima», «Lima › Miraflores»).
  const visibles = (terminos ?? [])
    .filter((t) => !filtro || (nombres.get(t.id) ?? t.nombre).toLowerCase().includes(filtro.toLowerCase()))
    .sort((a, b) => (nombres.get(a.id) ?? a.nombre).localeCompare(nombres.get(b.id) ?? b.nombre, 'es'))
  const alternar = (id: number) => cambiar(elegidos.includes(id) ? elegidos.filter((x) => x !== id) : [...elegidos, id])
  const puedeCrear = !soloLectura && esquemaDeTerminos?.puede?.crear !== false

  const crear = async () => {
    const nombre = nuevo.trim()
    if (!nombre || sitio === null) return
    setCreando(true)
    setFallo(null)
    try {
      const r = await pedir<{ doc: Termino }>('/terminos', { metodo: 'POST', cuerpo: { nombre, taxonomia: taxonomia.id, cliente: espacio, sitio } })
      await clienteDeConsultas.invalidateQueries({ queryKey: ['lista', 'terminos'] })
      cambiar([...elegidos, r.doc.id])
      setNuevo('')
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido crear. Vuelve a intentarlo.')
    } finally {
      setCreando(false)
    }
  }

  return (
    <Tarjeta className="flex flex-col gap-3">
      <div>
        <p className="text-[13px] font-medium">{taxonomia.nombrePlural}</p>
        {error && (
          <p className="mt-1 text-xs text-error" role="alert">
            {error}
          </p>
        )}
      </div>
      {isPending ? (
        <Esqueleto filas={3} />
      ) : (terminos ?? []).length === 0 ? (
        <p className="text-sm text-tenue">Todavía no hay ninguno.{puedeCrear ? ' Crea el primero aquí abajo.' : ''}</p>
      ) : (
        <>
          {(terminos ?? []).length > 12 && (
            <input aria-label={`Buscar en ${taxonomia.nombrePlural}`} className={claseDeEntrada(undefined, true)} onChange={(e) => setFiltro(e.target.value)} placeholder="Buscar…" type="search" value={filtro} />
          )}
          <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto">
            {visibles.map((t) => (
              <li key={t.id}>
                <label className="flex cursor-pointer items-center gap-2 text-sm">
                  <Casilla disabled={soloLectura} marcada={elegidos.includes(t.id)} onChange={() => alternar(t.id)} />
                  {nombres.get(t.id) ?? t.nombre}
                </label>
              </li>
            ))}
            {visibles.length === 0 && <li className="text-xs text-tenue">Ninguno se llama así.</li>}
          </ul>
        </>
      )}
      {puedeCrear && (
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <input
              aria-label={`Nombre del nuevo término de ${taxonomia.nombrePlural}`}
              className={claseDeEntrada(undefined, true)}
              onChange={(e) => setNuevo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void crear()
                }
              }}
              placeholder={`Nuevo: «${taxonomia.nombre}»…`}
              value={nuevo}
            />
            <Boton cargando={creando} disabled={!nuevo.trim()} icono={<Plus />} onClick={() => void crear()} pequeno>
              Crear
            </Boton>
          </div>
          {fallo && (
            <p className="text-xs text-error" role="alert">
              {fallo}
            </p>
          )}
        </div>
      )}
    </Tarjeta>
  )
}
